<template>
  <div class="map_wrapper">
    <label v-if="placeName" class="place-label">{{ placeName }}</label>
    <GmapMap
      ref="gmap"
      :center="mapCenter"
      :zoom="currentZoom"
      map-type-id="terrain"
      style="width: 100%; height: 400px"
      @click="onMapClick"
    >
      <GmapMarker
        v-if="!isPointsMode"
        :position="mapCenter"
        :clickable="true"
        :draggable="interactive"
        @dragend="onMarkerDragEnd"
      />

      <GmapMarker
        v-for="(point, index) in editablePolygonPoints"
        :key="`point-${index}`"
        :position="point"
        :draggable="true"
        @dragend="updatePointPosition(index, $event)"
        @rightclick="removePoint(index)"
      />

      <GmapPolygon
        v-if="isPointsMode && showPolygon && localPoints.length > 1"
        :path="localPoints"
        :options="polygonOptions"
      />

      <GmapMarker
        v-for="marker in normalizedLiveMarkers"
        :key="`live-${marker.id}`"
        :position="{ lat: marker.lat, lng: marker.lng }"
        :clickable="true"
        :draggable="false"
        :title="marker.name"
        :icon="getLiveMarkerIcon(marker)"
        :zIndex="1200"
        @click="selectedLiveMarkerId = marker.id"
      >
        <GmapInfoWindow
          :opened="selectedLiveMarkerId === marker.id"
          @closeclick="selectedLiveMarkerId = null"
        >
          <div class="live_marker_info">
            <strong>{{ marker.name }}</strong>
            <p v-if="marker.statusText">{{ marker.statusText }}</p>
          </div>
        </GmapInfoWindow>
      </GmapMarker>

      <GmapCircle
        v-if="!isPointsMode && radiusInMeters > 0"
        :center="mapCenter"
        :radius="radiusInMeters"
        :editable="false"
        :clickable="false"
      />
    </GmapMap>
  </div>
</template>

<script>
export default {
  name: "MapComponent",
  props: {
    lat: {
      type: Number,
      required: true,
    },
    lng: {
      type: Number,
      required: true,
    },
    radiusKm: {
      type: [Number, String],
      default: null,
    },
    interactive: {
      type: Boolean,
      default: true,
    },
    selectionMode: {
      type: String,
      default: "single",
      validator(value) {
        return ["single", "points"].includes(value);
      },
    },
    points: {
      type: Array,
      default: () => [],
    },
    showPolygon: {
      type: Boolean,
      default: true,
    },
    zoom: {
      type: Number,
      default: 12,
    },
    autoFitBounds: {
      type: Boolean,
      default: false,
    },
    liveMarkers: {
      type: Array,
      default: () => [],
    },
  },

  watch: {
    points: {
      immediate: true,
      handler(newPoints) {
        if (!this.isPointsMode) return;

        this.localPoints = this.normalizePoints(newPoints);
        this.fitMapToPoints();
      },
    },
  },

  data() {
    return {
      placeName: "",
      localPoints: [],
      currentZoom: this.zoom,
      selectedLiveMarkerId: null,
      polygonOptions: {
        fillColor: "#1f8cb7",
        fillOpacity: 0.2,
        strokeColor: "#1f8cb7",
        strokeOpacity: 0.9,
        strokeWeight: 2,
      },
    };
  },

  methods: {
    fitMapToPoints() {
      if (!this.autoFitBounds || !this.localPoints.length) {
        this.currentZoom = this.zoom;
        return;
      }

      this.$nextTick(() => {
        const mapRef = this.$refs.gmap;
        if (!mapRef?.$mapPromise) return;

        mapRef.$mapPromise.then((map) => {
          if (this.localPoints.length === 1) {
            map.setCenter(this.localPoints[0]);
            map.setZoom(this.zoom);
            return;
          }

          const bounds = new google.maps.LatLngBounds();
          this.localPoints.forEach((point) => bounds.extend(point));
          map.fitBounds(bounds);
        });
      });
    },

    normalizePoints(points) {
      if (!Array.isArray(points)) return [];

      return points
        .map((point) => {
          const lat = Number(point?.lat);
          const lng = Number(point?.lng ?? point?.long);

          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

          return { lat, lng };
        })
        .filter(Boolean);
    },

    emitPoints() {
      const pointsPayload = this.localPoints.map((point) => ({
        lat: point.lat,
        long: point.lng,
      }));

      this.$emit("update:points", pointsPayload);
    },

    onMarkerDragEnd(event) {
      if (!this.interactive) return;

      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();

      this.$emit("update:lat", newLat);
      this.$emit("update:lng", newLng);
      this.$emit("update:placeName", this.placeName);
      this.getPlaceName(newLat, newLng);
    },
    onMapClick(event) {
      if (!this.interactive) return;

      const clickedLat = event.latLng.lat();
      const clickedLng = event.latLng.lng();

      if (this.isPointsMode) {
        this.localPoints.push({ lat: clickedLat, lng: clickedLng });
        this.emitPoints();
        this.getPlaceName(clickedLat, clickedLng);
        return;
      }

      this.$emit("update:lat", clickedLat);
      this.$emit("update:lng", clickedLng);
      this.$emit("update:placeName", this.placeName);
      this.getPlaceName(clickedLat, clickedLng);
    },

    updatePointPosition(index, event) {
      if (!this.interactive) return;

      const { latLng } = event;
      this.$set(this.localPoints, index, {
        lat: latLng.lat(),
        lng: latLng.lng(),
      });
      this.emitPoints();
    },

    removePoint(index) {
      if (!this.interactive) return;

      this.localPoints.splice(index, 1);
      this.emitPoints();
    },

    async getPlaceName(lat, lng) {
      const geocoder = new google.maps.Geocoder();
      const latlng = { lat, lng };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === "OK" && results[0]) {
          this.placeName = results[0].formatted_address; // Set the place name
        } else {
          this.placeName = "Unknown Location"; // Fallback if no result
        }

        this.$emit("update:placeName", this.placeName);
      });
    },

    getLiveMarkerIcon(marker) {
      if (typeof google === "undefined" || !google.maps) return undefined;

      const fillColor =
        marker.is_inside === true
          ? "#22c55e"
          : marker.is_inside === false
          ? "#ef4444"
          : "#2563eb";

      return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor,
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      };
    },
  },

  computed: {
    isPointsMode() {
      return this.selectionMode === "points";
    },

    editablePolygonPoints() {
      return this.interactive ? this.localPoints : [];
    },

    mapCenter() {
      if (this.isPointsMode && this.localPoints.length) {
        const totalLat = this.localPoints.reduce(
          (sum, point) => sum + point.lat,
          0
        );
        const totalLng = this.localPoints.reduce(
          (sum, point) => sum + point.lng,
          0
        );

        return {
          lat: totalLat / this.localPoints.length,
          lng: totalLng / this.localPoints.length,
        };
      }

      return {
        lat: Number.isFinite(Number(this.lat)) ? Number(this.lat) : 24.7136,
        lng: Number.isFinite(Number(this.lng)) ? Number(this.lng) : 46.6753,
      };
    },

    radiusInMeters() {
      const radius = Number(this.radiusKm);
      if (!Number.isFinite(radius) || radius <= 0) return 0;

      return radius * 1000;
    },

    normalizedLiveMarkers() {
      if (!Array.isArray(this.liveMarkers)) return [];

      return this.liveMarkers
        .map((marker) => {
          const lat = Number(marker?.lat);
          const lng = Number(marker?.lng ?? marker?.long);

          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

          return {
            id: marker.id,
            name: marker.name || "",
            lat,
            lng,
            is_inside: marker.is_inside,
            statusText: marker.statusText || "",
          };
        })
        .filter(Boolean);
    },
  },
};
</script>

<style scoped>
.map_wrapper {
  width: 100%;
  height: 100%;
}

.place-label {
  display: block;
  margin: 20px 0;
  font-size: 16px;
  font-weight: bold;
}

.live_marker_info {
  min-width: 120px;
  color: #1f2937;
}

.live_marker_info p {
  margin: 4px 0 0;
}
</style>
